// =================================================================
// Signup page
//
// Individual self-serve signup for $4.99/month Brain Health.
//
// Flow:
//   1. User lands on /signup, sees email-only form
//   2. Submits → magic link sent to their email
//   3. They click the link, return to /signup
//   4. tryCompleteLinkSignIn() completes the Firebase Auth handshake
//   5. We check if they already have a /brain_health/users/{uid}/
//      record:
//        - YES (returning user) → redirect to /account
//        - NO (new user) → show display-name form
//   6. Display name submitted → write flat snake_case fields to
//      /brain_health/users/{uid}/ → /account
//
// Why two steps (email first, then display name): returning users
// shouldn't be made to re-enter their name every time they sign in.
// Email-link auth doesn't distinguish signup from signin, so we use
// the presence of a /users/{uid}/ record as the marker.
//
// Schema: flat snake_case fields directly under /brain_health/users/$uid/,
// matching the /brain_health/accounts/ convention. The rules
// (database.rules.json) require created_at as the existence marker;
// display_name, email, and acquisition_source are each validated by
// their own child rules when written.
//
// IMPORTANT: rules-cascade gotcha — DO NOT use a single multi-key
// update() at the $uid parent. The rules deliberately don't grant
// .write at /users/$uid (so subscription/entitlement stay server-
// only); they grant .write per child field instead. A parent-level
// update() needs .write at the parent path. Write each field with
// its own set() call instead.
//
// Gate logic note: the post-link-completion redirect decision is
// re-evaluated on every render where phase is one of the post-link
// phases, not just once. This protects against a race where
// hasUserRecord starts false (RTDB read hasn't landed yet) and we
// briefly enter the 'profile' phase, but then hasUserRecord flips
// true once useIndividualUser settles — at which point we redirect
// to /account properly. The phase set we re-evaluate against
// includes 'profile' specifically so that this late-arriving record
// doesn't get ignored.
//
// The actual subscription handoff to Stripe lives on /account, which
// shows a "Subscribe — $4.99/month" CTA when entitlementOk is false.
// =================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, set } from 'firebase/database';
import { useAuth } from '../lib/useAuth.jsx';
import { useIndividualUser } from '../lib/useIndividualUser.jsx';
import { db } from '../lib/firebase.js';
import './Signup.css';

export default function Signup() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    sendLoginLink,
    tryCompleteLinkSignIn,
  } = useAuth();
  const {
    isActive: hasUserRecord,
    loading: individualLoading,
  } = useIndividualUser();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  // Phases:
  //   'form'       — initial email-only form
  //   'sent'       — link sent, waiting for user to click it
  //   'completing' — handling the link-return, auth in progress
  //   'profile'    — new user, needs to provide display name
  //   'saving'     — writing the /users/ record
  const [phase, setPhase] = useState('form');
  const [error, setError] = useState(null);

  // On mount, check if this page load is a return-from-email-link.
  // tryCompleteLinkSignIn is a no-op if the URL doesn't look like a
  // sign-in link, so it's safe to call always.
  useEffect(() => {
    let cancelled = false;
    async function checkLinkCompletion() {
      const result = await tryCompleteLinkSignIn();
      if (cancelled) return;
      if (result.handled) {
        if (result.error) {
          setError(result.error);
          setPhase('form');
        } else {
          setPhase('completing');
        }
      }
    }
    checkLinkCompletion();
    return () => {
      cancelled = true;
    };
  }, [tryCompleteLinkSignIn]);

  // Post-link decision gate.
  //
  // Re-evaluates on every change to phase / loading flags / user /
  // hasUserRecord. Crucially, it runs not only when phase is
  // 'completing', but also when phase is 'profile' — so if we
  // briefly entered 'profile' because hasUserRecord hadn't loaded
  // yet, and it subsequently becomes true, we correct course and
  // redirect to /account. (Without this, the gate's one-shot
  // 'completing' check leaves the user stuck on the profile form
  // forever, asked for a name they already provided.)
  //
  // Also: if hasUserRecord is true, the user is signed in and has
  // a record, which means the magic-link flow effectively succeeded
  // even if tryCompleteLinkSignIn didn't get a chance to run (e.g.,
  // user navigated to /signup directly while already signed in).
  // Redirecting in that case is correct too — they don't need to
  // be on the signup page.
  useEffect(() => {
    if (authLoading || individualLoading) return;
    if (!user) return;

    // If user already has a record, they shouldn't see this page
    // regardless of which phase we're in (except 'saving', which is
    // mid-write and self-resolves to /account anyway).
    if (hasUserRecord && phase !== 'saving') {
      navigate('/account', { replace: true });
      return;
    }

    // Only auto-advance to 'profile' if we just completed a link
    // sign-in. Don't do this on a casual visit to /signup with no
    // record — they should see the email form.
    if (phase === 'completing' && !hasUserRecord) {
      setPhase('profile');
    }
  }, [phase, authLoading, individualLoading, user, hasUserRecord, navigate]);

  async function handleSendLink(e) {
    e.preventDefault();
    setError(null);
    try {
      await sendLoginLink(email, { returnTo: '/signup' });
      setPhase('sent');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setError(null);
    setPhase('saving');
    try {
      // Per-child set() calls — see header comment for the rules-
      // cascade reasoning. The rules grant .write at each child
      // path individually; a parent-level update() would be denied.
      //
      // Order: write created_at FIRST. Its rule requires !data.exists(),
      // which is true only on a brand-new record. Doing created_at
      // first also makes the $uid validate pass cleanly: that rule
      // requires the resulting node to have a created_at child, and
      // after this first write, it does.
      const base = `brain_health/users/${user.uid}`;
      await set(ref(db, `${base}/created_at`), Date.now());
      await set(ref(db, `${base}/email`), user.email);
      await set(ref(db, `${base}/display_name`), displayName.trim());
      await set(ref(db, `${base}/acquisition_source`), 'self_serve');

      // useIndividualUser will pick up the new record via its
      // onValue subscription. By the time /account mounts, the
      // context will be settled.
      navigate('/account', { replace: true });
    } catch (err) {
      setError(`Could not save your profile: ${err.message}`);
      setPhase('profile');
    }
  }

  // ----- RENDER PHASES -----

  if (phase === 'sent') {
    return (
      <div className="container signup-page">
        <span className="eyebrow fade-in-1">Brain Health</span>
        <h1 className="signup-title fade-in-2">
          Check your <em>email</em>.
        </h1>
        <p className="signup-lede fade-in-3">
          We sent a sign-in link to <strong>{email}</strong>. Open it on
          this device to continue. You can close this tab — the link
          will bring you back here.
        </p>
        <p className="signup-fine fade-in-4">
          Didn't get it? Check your spam folder, or{' '}
          <button
            type="button"
            onClick={() => {
              setPhase('form');
              setError(null);
            }}
            className="signup-link-button"
          >
            try a different email
          </button>
          .
        </p>
      </div>
    );
  }

  if (phase === 'completing' || phase === 'saving') {
    return (
      <div className="container signup-page">
        <p className="signup-loading">
          {phase === 'completing' ? 'Signing you in…' : 'Saving…'}
        </p>
      </div>
    );
  }

  if (phase === 'profile') {
    return (
      <div className="container signup-page">
        <span className="eyebrow fade-in-1">One more thing</span>
        <h1 className="signup-title fade-in-2">
          What should we <em>call you</em>?
        </h1>
        <p className="signup-lede fade-in-3">
          Just your first name is fine. This is how we'll greet you
          when you sign in.
        </p>

        <form onSubmit={handleSaveProfile} className="signup-form fade-in-4">
          <label htmlFor="display-name" className="signup-label">
            Your name
          </label>
          <input
            id="display-name"
            type="text"
            autoComplete="given-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Margaret"
            className="signup-input"
            required
            autoFocus
          />

          {error ? (
            <div className="signup-error" role="alert">
              <p className="signup-error-line">{error}</p>
            </div>
          ) : null}

          <button
            type="submit"
            className="btn signup-submit"
            disabled={!displayName.trim()}
          >
            Continue
          </button>
        </form>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div className="container signup-page">
      <span className="eyebrow fade-in-1">Brain Health</span>
      <h1 className="signup-title fade-in-2">
        Start your <em>brain health journey</em>.
      </h1>
      <p className="signup-lede fade-in-3">
        An evidence-based cognitive fitness program developed with
        Dr. Rob Winningham. $4.99/month. Cancel anytime.
      </p>

      <form onSubmit={handleSendLink} className="signup-form fade-in-4">
        <label htmlFor="signup-email" className="signup-label">
          Your email
        </label>
        <input
          id="signup-email"
          type="email"
          inputMode="email"
          autoCapitalize="off"
          autoComplete="email"
          autoCorrect="off"
          spellCheck="false"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="signup-input"
          required
          autoFocus
        />

        {error ? (
          <div className="signup-error" role="alert">
            <p className="signup-error-line">{error}</p>
          </div>
        ) : null}

        <button
          type="submit"
          className="btn signup-submit"
          disabled={!email}
        >
          Send sign-in link
        </button>
      </form>

      <p className="signup-fine">
        We'll send a link to your email. Click it on this device to
        continue. No password needed.
      </p>
    </div>
  );
}
